import axios, { AxiosError } from 'axios'
import type { AxiosAdapter, AxiosRequestConfig, RawAxiosHeaders } from 'axios'

import type { ApiEnvelope, ApiProblem } from '@/types/common'
import { localizedError } from '@/utils/errorMessages'
import { createRequestId } from '@/utils/requestId'
import { AuthStorageError, loadAuthSession, readAuthSession, sameSession, type AuthSessionSnapshot } from '@/utils/authSession'
export { AUTH_STORAGE_PREFIX, AUTH_TOKEN_KEY, AUTH_SESSION_KEY } from '@/utils/authSession'

let readPageSession: (() => AuthSessionSnapshot) | undefined
export function bindRequestSession(reader: (() => AuthSessionSnapshot) | undefined) {
  readPageSession = reader
}

export class ApiError extends Error {
  readonly code: string
  readonly requestId?: string
  readonly fieldErrors?: ApiProblem['fieldErrors']
  readonly status?: number
  readonly requestSent?: boolean

  constructor(problem: ApiProblem, options: { requestId?: string; status?: number; requestSent?: boolean } = {}) {
    super(localizedError(problem.code, problem.message))
    this.name = 'ApiError'
    this.code = problem.code
    this.requestId = options.requestId
    this.fieldErrors = Array.isArray(problem.fieldErrors)
      ? problem.fieldErrors.map((field) => ({ ...field, message: localizedError('VALIDATION_ERROR', field.message) }))
      : problem.fieldErrors
        ? Object.fromEntries(Object.entries(problem.fieldErrors).map(([key, message]) => [key, localizedError('VALIDATION_ERROR', message)]))
        : undefined
    this.status = options.status
    this.requestSent = options.requestSent
  }
}

const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api/v1',
  timeout: 12_000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

function isEnvelope<T>(payload: unknown): payload is ApiEnvelope<T> {
  return Boolean(
    payload &&
      typeof payload === 'object' &&
      'success' in payload &&
      'requestId' in payload &&
      'timestamp' in payload,
  )
}

function unwrap<T>(payload: ApiEnvelope<T> | T): T {
  if (!isEnvelope<T>(payload)) return payload
  if (!payload.success || payload.data === null) {
    throw new ApiError(payload.error ?? { code: 'UNKNOWN_ERROR', message: '請求未成功完成' }, {
      requestId: payload.requestId,
    })
  }
  return payload.data
}

function normalizeError(error: unknown, notifyUnauthorized = true): never {
  if (error instanceof ApiError) throw error
  if (error instanceof AuthStorageError) throw new ApiError({ code: error.code, message: error.message }, { requestSent: false })
  if (error instanceof AxiosError) {
    const responsePayload: unknown = error.response?.data
    if (isEnvelope<unknown>(responsePayload) && responsePayload.error) {
      if (responsePayload.error.code === 'PASSWORD_CHANGE_REQUIRED') window.dispatchEvent(new Event('auth:password-change-required'))
      if (notifyUnauthorized && error.response?.status === 401) window.dispatchEvent(new Event('auth:unauthorized'))
      throw new ApiError(responsePayload.error, {
        requestId: responsePayload.requestId,
        status: error.response?.status,
      })
    }
    if (notifyUnauthorized && error.response?.status === 401) window.dispatchEvent(new Event('auth:unauthorized'))
    throw new ApiError(
      {
        code: error.code ?? 'NETWORK_ERROR',
        message: error.response ? `請求失敗（HTTP ${error.response.status}）` : '無法連線至伺服器，請稍後再試',
      },
      { status: error.response?.status },
    )
  }
  throw new ApiError({ code: 'UNKNOWN_ERROR', message: '發生未知錯誤' })
}

export async function apiRequest<T>(config: AxiosRequestConfig): Promise<T> {
  let requestSession: AuthSessionSnapshot
  try { requestSession = readPageSession ? { ...readPageSession() } : loadAuthSession() }
  catch (cause) { return normalizeError(cause) }
  const requestToken = requestSession.token
  const sessionChanged = () => {
    try { return !sameSession(requestSession, readAuthSession()) || (readPageSession && !sameSession(requestSession, readPageSession())) }
    catch { return true }
  }
  const changedError = (requestSent: boolean) => new ApiError({ code: 'AUTH_SESSION_CHANGED', message: '登入帳號已變更，請重新操作' }, { requestSent })
  // A stale tab must never submit an old form using another tab's new credentials.
  if (sessionChanged()) throw changedError(false)
  const headers = axios.AxiosHeaders.from(config.headers as RawAxiosHeaders | undefined)
  if (requestToken) headers.set('Authorization', `Bearer ${requestToken}`)
  const options = { ...config, headers }
  if (requestToken && (config.method ?? 'GET').toUpperCase() === 'GET') {
    const params = new URLSearchParams(config.params instanceof URLSearchParams ? config.params : undefined)
    if (!(config.params instanceof URLSearchParams)) {
      for (const [key, value] of Object.entries(config.params ?? {})) {
        if (value !== null && value !== undefined) params.set(key, String(value))
      }
    }
    params.set('_request', createRequestId())
    options.params = params
  }
  try {
    const response = await client.request<ApiEnvelope<T> | T>(options)
    if (sessionChanged()) throw changedError(true)
    return unwrap(response.data)
  } catch (error) {
    if (sessionChanged()) throw changedError(true)
    return normalizeError(error)
  }
}

/** Only revokes the captured token. Its result must survive local logout without affecting a new login. */
export async function requestSessionLogout(token: string, adapter?: AxiosAdapter): Promise<void> {
  try {
    const response = await client.request({ method: 'POST', url: '/auth/logout', headers: { Authorization: `Bearer ${token}` }, ...(adapter ? { adapter } : {}) })
    if (response.status !== 204) throw new ApiError({ code: 'AUTH_SESSION_UNAVAILABLE', message: '伺服器未確認工作階段已失效。' }, { status: response.status })
  } catch (cause) {
    if (cause instanceof AxiosError && cause.response?.status === 401) return
    return normalizeError(cause, false)
  }
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? localizedError(error instanceof ApiError ? error.code : 'UNKNOWN_ERROR', error.message) : '操作失敗，請稍後再試'
}
